import React from 'react';
import { Calendar, Clock, User, Briefcase, DollarSign, AlertCircle } from 'lucide-react';
import { WizardData } from '../../types/bookingWizard.types';

interface SummaryStepProps {
    data: WizardData;
    professionalId: number;
    onConfirm: () => void;
    onBack?: () => void;
    loading?: boolean;
    error?: string;
}

export const SummaryStep: React.FC<SummaryStepProps> = ({
    data,
    onConfirm,
    onBack,
    loading,
    error
}) => {
    const formatDate = (): string => {
        const date = new Date(`${data.date}T${data.time}:00`);
        return date.toLocaleDateString('pt-BR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (timeStr: string): string => {
        return timeStr;
    };

    const formatDuration = (duration: string): string => {
        const match = duration.match(/(\d+):(\d+):\d+/);
        if (match) {
            const hours = parseInt(match[1]);
            const minutes = parseInt(match[2]);
            if (hours > 0) {
                return `${hours}h ${minutes > 0 ? minutes + 'min' : ''}`;
            }
            return `${minutes} min`;
        }
        return duration;
    };

    const formatPrice = (price: string): string => {
        const num = parseFloat(price);
        return num.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    const formatPhone = (phone: string): string => {
        if (phone.length === 11) {
            return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
        return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    };

    const beneficiaryName = data.isForDependent
        ? (data.dependent?.name || data.newDependentName)
        : data.client?.name;

    return (
        <div className="step-container">
            <h3>Resumo do Agendamento</h3>
            <p>Confira os dados antes de confirmar</p>

            <div className="summary-card">
                <div className="summary-section">
                    <div className="summary-icon">
                        <User size={20} />
                    </div>
                    <div className="summary-content">
                        <label>Cliente</label>
                        <p>{data.client?.name}</p>
                        <small>{formatPhone(data.client?.phone || '')}</small>
                    </div>
                </div>

                <div className="summary-section">
                    <div className="summary-icon">
                        <Calendar size={20} />
                    </div>
                    <div className="summary-content">
                        <label>Data</label>
                        <p>{formatDate()}</p>
                    </div>
                </div>

                <div className="summary-section">
                    <div className="summary-icon">
                        <Clock size={20} />
                    </div>
                    <div className="summary-content">
                        <label>Horário</label>
                        <p>{formatTime(data.time)}</p>
                    </div>
                </div>

                <div className="summary-section">
                    <div className="summary-icon">
                        <Briefcase size={20} />
                    </div>
                    <div className="summary-content">
                        <label>Serviço</label>
                        <p>{data.service?.name}</p>
                        <small>{formatDuration(data.service?.duration || '')}</small>
                    </div>
                </div>

                <div className="summary-section">
                    <div className="summary-icon">
                        <User size={20} />
                    </div>
                    <div className="summary-content">
                        <label>Beneficiário</label>
                        <p>{beneficiaryName}</p>
                        {data.isForDependent && <small>Dependente</small>}
                    </div>
                </div>

                <div className="summary-section total">
                    <div className="summary-icon">
                        <DollarSign size={20} />
                    </div>
                    <div className="summary-content">
                        <label>Valor Total</label>
                        <p className="price">{formatPrice(data.service?.price || '0')}</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            <div className="step-navigation">
                <button
                    className="btn-secondary"
                    onClick={onBack}
                    disabled={loading}
                >
                    Voltar
                </button>
                <button
                    className="btn-primary"
                    onClick={onConfirm}
                    disabled={loading}
                >
                    {loading ? 'Confirmando...' : 'Confirmar Agendamento'}
                </button>
            </div>
        </div>
    );
};

export default SummaryStep;
